import useSWR from "swr";
import { useEffect, useState } from "react";
import { fetcher, FetchError } from "../utils/fetcher";
import { Permission } from "./use-legacy-permission";
import { Tool } from "types/tool";
import { useRouter } from "next/router";

type User = {
  email: string;
  name: string;
  role: Permission;
  division: string;
  tools: Tool[];
};

type UseUserOptions = {
  redirect?: boolean;
};

export const useUser = ({ redirect }: UseUserOptions = {}): {
  user?: User;
  isAuthenticated: boolean;
  isLoaded: boolean;
} => {
  const { data, error } = useSWR(`/api/user`, fetcher, {
    revalidateOnMount: true,
  });

  const { replace } = useRouter();
  
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (error) {
      // Check if it's a FetchError with status code
      if (error instanceof FetchError) {
        // Only redirect to login on authentication errors (401, 403)
        if (error.status === 401 || error.status === 403) {
          setIsAuthenticated(false);
          setIsLoaded(true);
          replace("/login?error='Authentication required'");
        } else if (error.status >= 500) {
          // Server errors - keep current auth state, don't log out
          // Just mark as loaded
          setIsLoaded(true);
        } else {
          // Other errors (4xx except 401/403)
          setIsAuthenticated(false);
          setIsLoaded(true);
        }
      } else {
        // Non-fetch errors - treat as not authenticated
        setIsAuthenticated(false);
        setIsLoaded(true);
      }
      return;
    }
    
    if (data?.error) {
      setIsAuthenticated(false);
      setIsLoaded(true);
      return;
    }
    
    if (!data) {
      // Still loading
      return;
    }
    
    const userData = data.user;
    if (!userData) {
      setIsAuthenticated(false);
      setIsLoaded(true);
      return;
    }

    // Check auth version
    if (userData.authVersion !== 2) {
      replace("/logout");
      return;
    }

    // Set user data
    const authenticated = !!userData.tools.length;
    setUser(userData);
    setIsAuthenticated(authenticated);
    setIsLoaded(true);
    
    // Handle redirect for unauthenticated users
    if (!authenticated && redirect) {
      replace("/login?error='Your account does not have access to the evidence tool'");
    }
  }, [data, error, redirect, replace]);

  return { user, isAuthenticated, isLoaded };
};
